import React, {
    createRef,
    Component,
    ReactNode,
    MouseEventHandler,
    KeyboardEventHandler,
} from 'react';
import ReactDOM from 'react-dom';
import classnames from 'classnames';
import { addClassToNode, removeClassFromNode } from '../Common/domNodeModifiers';
import { ModalProvider, ModalConsumer } from './modalContext';
import { ModalRefContext, ModalContextProps } from './refContext';
import { FocusTrap } from '../Common/FocusTrap';

const isTest = process.env.NODE_ENV === 'test';

import styles from './main.scss';

// DBL uses header, footer, and main elements for content
// modals are appended to the body and are siblings to the above
// elements, so they will not be made inert
// TODO update DAL to use the same semantic elements
const PAGE_CONTENT_SELECTORS = [
    'header',
    'footer',
    'main',
    '#intercom-container',
    '#intercom-frame',
    '#back-to-top-button',
    // selectors for storybooks
    '.sb-wrapper ~ #root',
    '.sb-wrapper ~ #docs-root',
    '.sb-wrapper',
].join(', ');

function removeInert(): void {
    if (document) {
        Array.from(document.querySelectorAll(PAGE_CONTENT_SELECTORS)).forEach(node => {
            node.removeAttribute('inert');
        });
    }
}
function addInert(): void {
    if (document) {
        Array.from(document.querySelectorAll(PAGE_CONTENT_SELECTORS)).forEach(node => {
            node.setAttribute('inert', 'true');
        });
    }
}

type ModalContainerProps = {
    isOpen: boolean;
    onClose: () => void;
    animates?: boolean;
    appendToBody: boolean;
    children?: ReactNode;
    closeOnEsc?: boolean;
    closeOnOutsideClick?: boolean;
    _INTERNAL_ONLY_CUSTOM_CONTENT_?: ReactNode;
    dataTn?: string;
    modalClass?: string;
    modalPosition: 'top' | 'center' | 'bottom';
    modalSize: 'default' | 'medium' | 'large' | 'fullscreen';
    onOpen?: () => void;
    overlayClass?: string;
    preventBackScroll: boolean;
    removeModalOnClose?: boolean;
    animateFromBottom?: boolean;
    ariaLabel?: string;
    ariaLabelledBy?: string;
    trapFocus?: boolean;
};

type ModalContainerState = {
    modalOpened: boolean;
    animation: null | 'opening' | 'closing';
};

export class ModalContainer extends Component<ModalContainerProps, ModalContainerState> {
    static defaultProps = {
        animates: true,
        closeOnEsc: true,
        closeOnOutsideClick: true,
        modalPosition: 'top',
        modalSize: 'default',
        appendToBody: !isTest,
        preventBackScroll: true,
        removeModalOnClose: false,
        animateFromBottom: false,
        trapFocus: true,
        paused: false,
    };

    constructor(props: ModalContainerProps) {
        super(props);
        this.closeModal = this.closeModal.bind(this);
        this.openModal = this.openModal.bind(this);
        this.removeModal = this.removeModal.bind(this);
        this.endCloseAnimation = this.endCloseAnimation.bind(this);
        this.handleAnimationEnd = this.handleAnimationEnd.bind(this);
        this.timeouts = [];
        this.scrollY = 0;
        this.state = {
            modalOpened: false,
            animation: null,
        };
        this.portal = null;
    }

    componentDidMount(): void {
        if (this.props.isOpen && !this.state.modalOpened) {
            this.openModal();
        }
    }
    UNSAFE_componentWillReceiveProps(nextProps: ModalContainerProps): void {
        if (nextProps.isOpen && !this.state.modalOpened) {
            this.openModal();
        } else if (!nextProps.isOpen && this.state.modalOpened) {
            this.closeModal();
        }
    }

    componentWillUnmount(): void {
        this.clearTimeouts();
        const { preventBackScroll } = this.props;
        this.removeModal();
        if (this.state.modalOpened) {
            removeInert();
        }
        if (preventBackScroll) {
            this.unlockScroll();
        }
    }

    clearTimeouts(): void {
        this.timeouts.forEach(clearTimeout);
    }

    modalRef: ModalContextProps = createRef();
    isMouseDown: boolean = false;
    portal: HTMLDivElement | null;
    timeouts: NodeJS.Timeout[];
    scrollY: number;
    trigger: HTMLElement | undefined;

    removeModal(): void {
        const { appendToBody } = this.props;
        if (appendToBody && document.body && this.portal) {
            document.body.removeChild(this.portal);
            this.portal = null;
        }
    }

    lockScroll(): void {
        this.scrollY = window.pageYOffset;
        addClassToNode({ domNode: document.body, className: styles.stopBodyScroll });
        // No need to scroll if position doesn't change - look at `.stopBodyScroll` styles
        if (this.scrollY === window.pageYOffset) {
            this.scrollY = 0;
        }
    }

    unlockScroll(): void {
        removeClassFromNode({ domNode: document.body, className: styles.stopBodyScroll });
        // Scroll back to previous position
        if (this.scrollY) {
            window.scrollTo(0, this.scrollY);
        }
    }

    handleAnimationEnd(): void {
        const { modalOpened, animation } = this.state;
        if (modalOpened && animation === 'opening' && this.props.preventBackScroll) {
            this.lockScroll();
        }
    }

    openModal(): void {
        const { closeOnEsc, preventBackScroll, animates, onOpen, appendToBody } = this.props;
        if (document.activeElement instanceof HTMLElement) {
            this.trigger = document.activeElement;
        }
        if (appendToBody) {
            if (!this.portal) {
                this.portal = document.createElement('div');
            }
            if (document.body) {
                document.body.appendChild(this.portal);
            }
            addInert();
        }

        if (closeOnEsc) {
            /*
                using @ts-ignore otherwise getting the following error:
                Argument of type '(e: KeyboardEvent<Element>) => void' is not assignable to parameter of type 'EventListenerOrEventListenerObject'.
                    Type '(e: KeyboardEvent<Element>) => void' is not assignable to type 'EventListener'.
                Types of parameters 'e' and 'evt' are incompatible.
                    Type 'Event' is missing the following properties from type 'KeyboardEvent<Element>': altKey, charCode, ctrlKey, getModifierState, and 12 more.
            */
            // @ts-ignore
            document.addEventListener('keydown', this.handleKeydown);
        }

        if (preventBackScroll && !this.props.animates) {
            this.lockScroll();
        }

        if (onOpen) {
            onOpen();
        }

        if (animates) {
            this.setState(
                {
                    modalOpened: true,
                },
                () => {
                    this.timeouts.push(
                        setTimeout(() => this.setState({ animation: 'opening' }), 10)
                    );
                }
            );
        } else {
            this.setState({
                modalOpened: true,
            });
        }
    }

    endCloseAnimation(): void {
        this.setState({ modalOpened: false, animation: null });
        if (this.props.removeModalOnClose) {
            this.removeModal();
        }
    }

    closeModal(): void {
        const { closeOnEsc, preventBackScroll, animates, removeModalOnClose } = this.props;
        removeInert();

        if (closeOnEsc) {
            /*
                using @ts-ignore otherwise getting the following error:
                Argument of type '(e: KeyboardEvent<Element>) => void' is not assignable to parameter of type 'EventListenerOrEventListenerObject'.
                    Type '(e: KeyboardEvent<Element>) => void' is not assignable to type 'EventListener'.
                Types of parameters 'e' and 'evt' are incompatible.
                    Type 'Event' is missing the following properties from type 'KeyboardEvent<Element>': altKey, charCode, ctrlKey, getModifierState, and 12 more.
            */
            // @ts-ignore
            document.removeEventListener('keydown', this.handleKeydown);
        }

        if (preventBackScroll) {
            this.unlockScroll();
        }

        if (animates && this.state.modalOpened) {
            this.setState(
                {
                    animation: 'closing',
                },
                () => {
                    this.timeouts.push(setTimeout(this.endCloseAnimation, 300));
                }
            );
        } else {
            this.setState({
                modalOpened: false,
            });
            if (removeModalOnClose) {
                this.removeModal();
            }
        }
    }

    handleKeydown: KeyboardEventHandler = e => {
        switch (e.key) {
            case 'Esc':
            case 'Escape': {
                // prevent [ESC] keydown events on inputs from closing the modal
                const isInputTarget =
                    e.target instanceof HTMLElement && e.target.tagName === 'INPUT';
                if (this.props.closeOnEsc && !isInputTarget) {
                    this.props.onClose();
                }
                break;
            }
            default:
                break;
        }
    };

    handleOutsideClick: MouseEventHandler = e => {
        const { closeOnOutsideClick, onClose } = this.props;
        e.stopPropagation();
        if (closeOnOutsideClick) {
            onClose();
        }
    };

    handleInsideClick: MouseEventHandler = e => {
        e.stopPropagation();
    };

    render(): ReactNode {
        const {
            animates,
            appendToBody,
            children,
            _INTERNAL_ONLY_CUSTOM_CONTENT_,
            dataTn,
            modalClass,
            modalPosition,
            modalSize,
            overlayClass,
            animateFromBottom,
            ariaLabel,
            ariaLabelledBy,
            trapFocus,
        } = this.props;
        const { modalOpened, animation } = this.state;

        const overlayClasses = classnames(
            styles.overlayDefault,
            {
                [styles.overlayFadeStart]: animates,
                [styles.overlayDefaultOpening]: animates && animation === 'opening',
                [styles.overlayDefaultClosing]: animates && animation === 'closing',
                [styles.modalHidden]: !modalOpened,
            },
            overlayClass
        );

        let modalClasses = '';
        if (!_INTERNAL_ONLY_CUSTOM_CONTENT_) {
            const isModalPositionBottom = modalPosition === 'bottom';
            modalClasses = classnames(
                styles.modalDefault,
                {
                    [styles.modalMedium]: modalSize === 'medium',
                    [styles.modalLarge]: modalSize === 'large',
                    [styles.modalFullscreen]: modalSize === 'fullscreen',
                    [styles.modalHeightCenter]: modalPosition === 'center',
                    [styles.modalHeightBottomHidden]: isModalPositionBottom && animateFromBottom,
                    [styles.modalHeightBottomVisible]: isModalPositionBottom && !animateFromBottom,
                    [styles.animateFromBottom]:
                        isModalPositionBottom && animates && animation === 'opening',
                },
                modalClass
            );
        }

        const wrapperDataTn =
            (dataTn || 'modal-container') + '-' + (modalOpened ? 'open' : 'closed');
        const modalSelector = `modal-container-${modalOpened ? 'open' : 'closed'}`;
        const modal = (
            <ModalProvider>
                <ModalConsumer>
                    {({ labelUid, modalUid }) => (
                        <FocusTrap
                            options={{
                                autoFocusFirstTabbable: false,
                                fallbackFocus: `#${modalUid}`,
                                returnFocusOnDeactivate: true,
                                clickOutsideDeactivates: true,
                                setReturnFocus: trapFocus ? this.trigger : undefined,
                            }}
                            active={trapFocus && modalOpened && animation !== 'closing'}
                        >
                            <div
                                onClick={this.handleOutsideClick}
                                id={modalUid + 'Overlay'}
                                aria-modal="true"
                                role="dialog"
                                data-tn={wrapperDataTn}
                                data-modal-selector={modalSelector}
                                className={overlayClasses}
                                onTransitionEnd={this.handleAnimationEnd}
                                aria-label={ariaLabel}
                                aria-labelledby={
                                    (!ariaLabel && (ariaLabelledBy || labelUid)) || undefined
                                }
                            >
                                <div
                                    // data-dibs-modal is a stable attribute to select the modal.
                                    // replaces id="Modal", which has been changed into a dynamic uid.
                                    data-dibs-modal
                                    id={modalUid}
                                    tabIndex={-1}
                                    onClick={this.handleInsideClick}
                                    className={modalClasses}
                                    data-tn={dataTn}
                                    ref={this.modalRef}
                                >
                                    <ModalRefContext.Provider value={this.modalRef}>
                                        {_INTERNAL_ONLY_CUSTOM_CONTENT_ || children}
                                    </ModalRefContext.Provider>
                                </div>
                            </div>
                        </FocusTrap>
                    )}
                </ModalConsumer>
            </ModalProvider>
        );
        if (appendToBody && this.portal) {
            return ReactDOM.createPortal(modal, this.portal);
        }

        return modalOpened && modal;
    }
}
